// Dépôt d'un dossier au guichet pour un usager présent : le même parcours en étapes que le
// portail (démarche, usager, formulaire rendu par le moteur partagé, pièces, relecture), le dépôt
// validé par le serveur avec la version publiée de la démarche. Brouillon conservé sur l'appareil.
import type { Demarche, DonneesFormulaire } from '@cc/contrats';
import type { DossierDetail } from '@cc/contrats/ui-metier';
import { Bandeau, Bouton, Champ, Formulaire, Panneau, Selection, useBrouillon } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { requete } from '../../api/client';
import { ErreurApi } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { AffichageErreur } from '../../commun/EtatRequete';
import { ModuleNonActive, useModuleActif } from '../../commun/GardeModule';
import { useMessages } from '../../commun/Messages';
import { AffichageDonnees } from '../../formulaire/AffichageDonnees';
import { RenduFormulaire, validerSaisie, valeursInitiales } from '../../formulaire/RenduFormulaire';
import { useServices, useVersion } from '../demarches/contenu';
import { Progression } from './guichet/Progression';
import { EtapePieces } from './guichet/EtapePieces';
import { EtapeUsager } from './guichet/EtapeUsager';
import {
  etapePrecedente,
  etapeSuivante,
  LIBELLES_ETAPES_GUICHET,
  lireVersionGuichet,
  piecesManquantes,
  reglesNonRemplies,
  SAISIE_VIDE,
  type EtapeGuichet,
  type PieceDeposee,
  type SaisieGuichet,
} from './guichet/etapes';
import commun from '../../commun/commun.module.css';
import styles from './metier.module.css';

export function PageNouveauDossier() {
  const navigate = useNavigate();
  const { afficher } = useMessages();
  const moduleDemarches = useModuleActif('M05');
  const [etape, setEtape] = useState<EtapeGuichet>('demarche');
  const [s, setS] = useState<SaisieGuichet>(() => ({
    ...SAISIE_VIDE,
    capture_id: crypto.randomUUID(),
  }));
  const [pieces, setPieces] = useState<PieceDeposee[]>([]);
  const [erreursFormulaire, setErreursFormulaire] = useState<{ champ: string; message: string }[]>(
    [],
  );
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);
  const brouillon = useBrouillon('dossier-guichet', {
    ...s,
    usager: s.usager ? { ...s.usager } : null,
  });
  const demarches = useQuery({
    queryKey: ['demarches', 'liste'],
    queryFn: ({ signal }) =>
      requete<{ elements: Demarche[] }>('/demarches', { signal }).then((r) => r.elements),
    retry: false,
  });
  const services = useServices();
  const demarche = demarches.data?.find((d) => d.id === s.demarche_id);
  const version = useVersion(demarche?.id, demarche?.version_courante?.numero);
  const v = useMemo(() => lireVersionGuichet(version.data?.contenu), [version.data]);
  const maj = (patch: Partial<SaisieGuichet>) =>
    setS((x) => {
      const autreUsager =
        (patch.usager !== undefined && patch.usager?.id !== x.usager?.id) ||
        (patch.mode_usager !== undefined && patch.mode_usager !== x.mode_usager) ||
        (patch.tiers_nom !== undefined && patch.tiers_nom !== x.tiers_nom);
      return { ...x, ...patch, capture_id: autreUsager ? crypto.randomUUID() : x.capture_id };
    });

  const choisirDemarche = (id: string) => {
    const d = demarches.data?.find((x) => x.id === id);
    setS((x) => ({
      ...x,
      demarche_id: id,
      demarche_version_id: d?.version_courante?.id ?? '',
      donnees: {},
    }));
    setPieces([]);
  };
  const donneesInitialisees =
    v?.formulaire && Object.keys(s.donnees).length === 0
      ? valeursInitiales(v.formulaire)
      : s.donnees;

  const usagerPret =
    s.mode_usager === 'tiers'
      ? s.tiers_nom.trim().length > 0
      : Boolean(s.usager) && (!v?.identiteVerifieeRequise || Boolean(s.usager?.identite_verifiee));
  const validerFormulaire = (): boolean => {
    if (!v) return false;
    const regles = reglesNonRemplies(v, donneesInitialisees);
    if (v.formulaire) {
      const r = validerSaisie(v.formulaire, donneesInitialisees, { tarifs: v.tarifs });
      const erreurs = [...r.erreurs, ...regles.map((m) => ({ champ: 'eligibilite', message: m }))];
      setErreursFormulaire(erreurs);
      return erreurs.length === 0;
    }
    const manquants = v.champsSimples
      .filter((c) => c.obligatoire && !donneesInitialisees[c.code])
      .map((c) => ({ champ: c.code, message: `${c.libelle} : champ obligatoire` }));
    setErreursFormulaire(manquants);
    return manquants.length === 0;
  };

  const suivant = () => {
    if (etape === 'demarche' && !s.demarche_version_id) return;
    if (etape === 'usager' && !usagerPret) return;
    if (etape === 'formulaire' && !validerFormulaire()) return;
    setEtape(etapeSuivante(etape));
  };

  const deposer = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      const corps: Record<string, unknown> = {
        demarche_version_id: s.demarche_version_id,
        donnees_formulaire: donneesInitialisees,
        pieces: pieces.map((p) => ({ document_id: p.document_id, type: p.type })),
        service_id: s.service_id || undefined,
      };
      if (s.mode_usager === 'compte' && s.usager) corps.usager_compte_id = s.usager.id;
      else {
        corps.tiers_nom = s.tiers_nom;
        if (s.telephone || s.courriel)
          corps.tiers_contact = {
            telephone: s.telephone || undefined,
            courriel: s.courriel || undefined,
          };
      }
      const cree = await requete<DossierDetail>('/dossiers', { methode: 'POST', corps });
      brouillon.effacer();
      afficher(
        'succes',
        `Dossier déposé sous le numéro ${cree.numero ?? "(numéro en cours d'attribution)"}.`,
      );
      navigate(`/dossiers/${cree.id}`);
    } catch (e) {
      setErreur(e);
      if (
        e instanceof ErreurApi &&
        e.champs.some((c) => c.champ.startsWith('donnees_formulaire.'))
      ) {
        setErreursFormulaire(
          e.champs.map((c) => ({
            champ: c.champ.replace('donnees_formulaire.', ''),
            message: c.message,
          })),
        );
        setEtape('formulaire');
      }
    } finally {
      setEnCours(false);
    }
  };

  const publiees = (demarches.data ?? []).filter((d) => d.etat === 'publiee' && d.version_courante);
  const manquantes = v ? piecesManquantes(v, donneesInitialisees, pieces) : [];

  // Module M05 désactivé (H9-01) : aucun nouveau dépôt ; les dossiers existants restent lisibles et instruits.
  if (moduleDemarches === false)
    return <ModuleNonActive module="M05" retour={{ href: '/dossiers', libelle: 'Dossiers' }} />;

  return (
    <>
      <EnTetePage
        etiquette="Dossiers"
        titre="Dépôt au guichet"
        description="Même parcours que le portail citoyen : la demande est validée par le serveur avec les règles de la version publiée de la démarche. Le numéro et les échéances sont attribués au dépôt."
      />
      <Progression courante={etape} />
      {erreur instanceof ErreurApi && erreur.routeIndisponible ? (
        <AffichageErreur erreur={erreur} lot="L0-C" fonction="Le dépôt des dossiers" />
      ) : erreur instanceof ErreurApi ? (
        <Bandeau type="erreur">
          {erreur.message}
          {erreur.champs.length > 0
            ? ` : ${erreur.champs.map((c) => `${c.champ} (${c.message})`).join(' ; ')}`
            : ''}
        </Bandeau>
      ) : erreur ? (
        <Bandeau type="erreur">Le dépôt a échoué.</Bandeau>
      ) : null}
      <Panneau>
        <Formulaire
          libelle={`Dépôt au guichet, étape ${LIBELLES_ETAPES_GUICHET[etape]}`}
          onSoumettre={() => (etape === 'relecture' ? void deposer() : suivant())}
          brouillonEnregistreLe={brouillon.enregistreLe}
          restauration={
            brouillon.brouillonDisponible && etape === 'demarche'
              ? {
                  date: brouillon.brouillonDate,
                  onReprendre: () => {
                    setS({
                      ...SAISIE_VIDE,
                      ...(brouillon.brouillonDisponible as SaisieGuichet),
                      capture_id:
                        (brouillon.brouillonDisponible as SaisieGuichet).capture_id ??
                        crypto.randomUUID(),
                    });
                    brouillon.ignorer();
                  },
                  onIgnorer: brouillon.effacer,
                }
              : null
          }
          actions={
            <>
              {etape !== 'demarche' ? (
                <Bouton variante="secondaire" onClick={() => setEtape(etapePrecedente(etape))}>
                  Étape précédente
                </Bouton>
              ) : null}
              {etape === 'relecture' ? (
                <Bouton type="submit" chargement={enCours} disabled={manquantes.length > 0}>
                  Déposer le dossier
                </Bouton>
              ) : (
                <Bouton
                  type="submit"
                  disabled={
                    (etape === 'demarche' && (!s.demarche_version_id || version.isPending)) ||
                    (etape === 'usager' && !usagerPret)
                  }
                >
                  Continuer
                </Bouton>
              )}
              <Bouton variante="discret" onClick={() => navigate('/dossiers')}>
                Annuler
              </Bouton>
            </>
          }
        >
          {etape === 'demarche' ? (
            <div className={styles.pile}>
              {demarches.error ? (
                <AffichageErreur
                  erreur={demarches.error}
                  lot="L1-A"
                  fonction="La liste des démarches"
                />
              ) : null}
              <Selection
                id="dossier-demarche"
                libelle="Démarche"
                optionVide="Choisir une démarche publiée"
                value={s.demarche_id}
                onChange={(e) => choisirDemarche(e.target.value)}
                options={publiees.map((d) => ({
                  valeur: d.id,
                  libelle: `${d.libelle}${d.interne ? ' (interne de test)' : ''}`,
                }))}
                required
                aide={
                  demarches.isPending
                    ? 'Chargement des démarches'
                    : `${publiees.length} démarche(s) publiée(s)`
                }
              />
              {demarche && v ? (
                <dl className={commun.definitions}>
                  <dt>Version</dt>
                  <dd>Version {demarche.version_courante?.numero} publiée</dd>
                  <dt>Conditions</dt>
                  <dd>{v.texteConditions || 'Aucune condition particulière'}</dd>
                  <dt>Pièces</dt>
                  <dd>
                    {v.pieces.length > 0
                      ? v.pieces
                          .map(
                            (p) =>
                              `${p.libelle}${p.obligatoire ? '' : ' (facultative)'}${p.condition ? ' (selon la situation)' : ''}`,
                          )
                          .join(', ')
                      : 'aucune'}
                  </dd>
                  <dt>Identité</dt>
                  <dd>
                    {v.identiteVerifieeRequise
                      ? 'Vérifiée sur pièce obligatoire'
                      : 'Déclarée suffisante'}
                  </dd>
                </dl>
              ) : null}
              <Selection
                id="dossier-service"
                libelle="Service de dépôt"
                optionVide="Service de la première étape (par défaut)"
                value={s.service_id}
                facultatif
                options={(services.data ?? []).map((x) => ({ valeur: x.id, libelle: x.nom }))}
                onChange={(e) => maj({ service_id: e.target.value })}
              />
            </div>
          ) : null}
          {etape === 'usager' ? (
            <EtapeUsager
              saisie={s}
              onChanger={maj}
              identiteRequise={Boolean(v?.identiteVerifieeRequise)}
            />
          ) : null}
          {etape === 'formulaire' && v ? (
            <div className={styles.pile}>
              {erreursFormulaire
                .filter((e) => e.champ === 'eligibilite')
                .map((e, i) => (
                  <Bandeau key={i} type="attention">
                    Conditions non remplies : {e.message}
                  </Bandeau>
                ))}
              {v.formulaire ? (
                <RenduFormulaire
                  formulaire={v.formulaire}
                  valeurs={donneesInitialisees}
                  onChanger={(d: DonneesFormulaire) => {
                    maj({ donnees: d });
                    setErreursFormulaire([]);
                  }}
                  erreurs={erreursFormulaire}
                  tarifs={v.tarifs}
                />
              ) : (
                v.champsSimples.map((c) => (
                  <Champ
                    key={c.code}
                    id={`champ-${c.code}`}
                    libelle={c.libelle}
                    required={c.obligatoire}
                    value={String(donneesInitialisees[c.code] ?? '')}
                    erreur={erreursFormulaire.find((e) => e.champ === c.code)?.message}
                    onChange={(e) =>
                      maj({ donnees: { ...donneesInitialisees, [c.code]: e.target.value } })
                    }
                  />
                ))
              )}
            </div>
          ) : null}
          {etape === 'pieces' && v ? (
            <EtapePieces
              version={v}
              saisie={{ ...s, donnees: donneesInitialisees }}
              pieces={pieces}
              onChanger={setPieces}
            />
          ) : null}
          {etape === 'relecture' && v ? (
            <div className={styles.pile}>
              <dl className={commun.definitions}>
                <dt>Démarche</dt>
                <dd>
                  {demarche?.libelle}, version {demarche?.version_courante?.numero}
                </dd>
                <dt>Demandeur</dt>
                <dd>
                  {s.mode_usager === 'compte'
                    ? `${[s.usager?.prenoms, s.usager?.nom].filter(Boolean).join(' ')} (${s.usager?.identifiant})`
                    : `${s.tiers_nom} (sans compte)`}
                </dd>
                <dt>Pièces</dt>
                <dd>{pieces.length > 0 ? pieces.map((p) => p.titre).join(', ') : 'aucune'}</dd>
              </dl>
              {manquantes.length > 0 ? (
                <Bandeau type="attention">
                  Pièces obligatoires manquantes : {manquantes.join(', ')}. Revenez à l'étape des
                  pièces.
                </Bandeau>
              ) : null}
              <AffichageDonnees
                formulaire={v.formulaire}
                donnees={donneesInitialisees}
                pieces={Object.fromEntries(pieces.map((p) => [p.document_id, p.titre]))}
              />
            </div>
          ) : null}
        </Formulaire>
      </Panneau>
    </>
  );
}
